package com.example.readingnotes.presentation.postlist

import com.example.readingnotes.domain.usecase.LoadPopularTagsUseCase
import com.example.readingnotes.domain.usecase.LoadPostListUseCase
import com.example.readingnotes.domain.usecase.LoadPostListWithPaginationUseCase
import com.example.readingnotes.domain.usecase.SearchPostsUseCase
import com.example.readingnotes.util.ErrorMessageProvider
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class PostListViewModel(
    private val loadPostListUseCase: LoadPostListUseCase,
    private val loadPostListWithPaginationUseCase: LoadPostListWithPaginationUseCase,
    private val searchPostsUseCase: SearchPostsUseCase,
    private val loadPopularTagsUseCase: LoadPopularTagsUseCase,
    private val viewModelScope: CoroutineScope,
) {
    private val _state = MutableStateFlow(PostListState())
    val state: StateFlow<PostListState> = _state.asStateFlow()

    fun onEvent(event: PostListEvent) {
        when (event) {
            is PostListEvent.OnAppear -> {
                loadPosts(isRefresh = false)
                loadPopularTags()
            }
            is PostListEvent.OnRefresh -> loadPosts(isRefresh = true)
            is PostListEvent.OnLoadMore -> loadMorePosts()
            is PostListEvent.OnRetry -> loadPosts(isRefresh = false)
            is PostListEvent.OnSearchQueryChanged -> {
                _state.update {
                    it.copy(
                        searchQuery = event.query,
                        isSearchActive = event.query.isNotBlank(),
                    )
                }
                // Auto-search after debounce
                if (event.query.isNotBlank()) {
                    searchPosts()
                } else {
                    _state.update {
                        it.copy(
                            isSearchActive = false,
                            searchResults = emptyList(),
                        )
                    }
                }
            }
            is PostListEvent.OnSearchSubmit -> searchPosts()
            is PostListEvent.OnClearSearch -> {
                _state.update {
                    it.copy(
                        searchQuery = "",
                        isSearchActive = false,
                        searchResults = emptyList(),
                    )
                }
            }
            is PostListEvent.OnSortChanged -> {
                _state.update { it.copy(sortBy = event.sortBy) }
                if (_state.value.isSearchActive) {
                    searchPosts()
                } else {
                    loadPosts(isRefresh = true)
                }
            }
            is PostListEvent.OnStatusFilterChanged -> {
                _state.update { it.copy(statusFilter = event.status) }
                if (_state.value.isSearchActive) {
                    searchPosts()
                } else {
                    loadPosts(isRefresh = true)
                }
            }
            is PostListEvent.OnTagFilterToggled -> {
                val currentTags = _state.value.selectedTags
                val newTags =
                    if (currentTags.contains(event.tag)) {
                        currentTags - event.tag
                    } else {
                        currentTags + event.tag
                    }
                _state.update { it.copy(selectedTags = newTags) }

                if (_state.value.isSearchActive || newTags.isNotEmpty()) {
                    searchPosts()
                }
            }
            is PostListEvent.OnClearTagFilters -> {
                _state.update { it.copy(selectedTags = emptySet()) }
                if (_state.value.isSearchActive) {
                    searchPosts()
                } else {
                    loadPosts(isRefresh = true)
                }
            }
            is PostListEvent.OnPopularTagsLoaded -> {
                _state.update { it.copy(popularTags = event.tags) }
            }
        }
    }

    private fun loadPosts(isRefresh: Boolean) {
        viewModelScope.launch {
            val page = if (isRefresh) 1 else _state.value.currentPage
            _state.update {
                if (isRefresh) {
                    it.copy(isRefreshing = true, currentPage = 1)
                } else {
                    it.copy(isLoading = true, errorMessage = null)
                }
            }

            loadPostListWithPaginationUseCase(page)
                .onSuccess { paginatedPosts ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isRefreshing = false,
                            posts = if (isRefresh) paginatedPosts.posts else paginatedPosts.posts,
                            currentPage = paginatedPosts.currentPage,
                            totalPages = paginatedPosts.totalPages,
                            hasMore = paginatedPosts.hasMore,
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isRefreshing = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "読み込みに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun loadMorePosts() {
        val currentState = _state.value
        if (currentState.isLoadingMore || !currentState.hasMore) return

        viewModelScope.launch {
            _state.update { it.copy(isLoadingMore = true) }

            val nextPage = currentState.currentPage + 1
            loadPostListWithPaginationUseCase(nextPage)
                .onSuccess { paginatedPosts ->
                    _state.update {
                        it.copy(
                            isLoadingMore = false,
                            posts = currentState.posts + paginatedPosts.posts,
                            currentPage = paginatedPosts.currentPage,
                            totalPages = paginatedPosts.totalPages,
                            hasMore = paginatedPosts.hasMore,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoadingMore = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "追加読み込みに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun searchPosts() {
        val currentState = _state.value
        if (currentState.searchQuery.isBlank()) return

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            val sortByString =
                when (currentState.sortBy) {
                    SortOption.OLDEST -> "oldest"
                    SortOption.TITLE_ASC -> "title_asc"
                    SortOption.TITLE_DESC -> "title_desc"
                    else -> "newest"
                }

            searchPostsUseCase(
                query = currentState.searchQuery,
                sortBy = sortByString,
                status = currentState.statusFilter,
                tags = currentState.selectedTags,
            ).onSuccess { results ->
                _state.update {
                    it.copy(
                        isLoading = false,
                        searchResults = results,
                        errorMessage = null,
                    )
                }
            }.onFailure { error ->
                _state.update {
                    it.copy(
                        isLoading = false,
                        errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "検索に失敗しました"),
                    )
                }
            }
        }
    }

    private fun loadPopularTags() {
        viewModelScope.launch {
            _state.update { it.copy(isLoadingTags = true) }

            loadPopularTagsUseCase()
                .onSuccess { tags ->
                    _state.update {
                        it.copy(
                            isLoadingTags = false,
                            popularTags = tags,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoadingTags = false,
                        )
                    }
                }
        }
    }
}
